import { isArrayNullOrEmpty } from '@farmshare/utils';
import { concat, join, map } from 'lodash';
import { useCallback } from 'react';

import { InputPhone, InputSelect, InputText } from '../inputs';
import { Divider, Stack } from '@mui/material';

export interface AddressFormForm extends Record<string, string | undefined> {
  type: 'residential' | 'commercial' | 'unknown';
  firstName?: string;
  lastName?: string;
  company?: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  postcode: string;
  email?: string;
  phone?: string;
}

export interface AddressFormProps {
  disabled?: boolean;
  floatingLabel?: boolean;
  prefixes?: string[];
  readOnly?: boolean;
  states?: {
    name: string;
    code: string;
  }[];
}

export function AddressForm({
  disabled,
  floatingLabel,
  prefixes,
  readOnly,
  states,
}: AddressFormProps) {
  const nameOverride = useCallback(
    (name: string) => {
      if (!isArrayNullOrEmpty(prefixes)) {
        return join(concat(prefixes, name), '.');
      }

      return name;
    },
    [prefixes],
  );

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1}>
        <InputText
          label="First Name"
          nameOverride={nameOverride('firstName')}
          type="text"
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
          required
        />
        <InputText
          label="Last Name"
          nameOverride={nameOverride('lastName')}
          type="text"
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
          required
        />
      </Stack>
      <InputText
        label="Company"
        nameOverride={nameOverride('company')}
        type="text"
        floatingLabel={floatingLabel}
        readOnly={readOnly}
        disabled={disabled}
      />
      <Stack direction="row" spacing={1}>
        <InputText
          label="Email"
          nameOverride={nameOverride('email')}
          type="email"
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
          required
        />
        <InputPhone
          label="Phone"
          nameOverride={nameOverride('phone')}
          floatingLabel={floatingLabel}
          required
          readOnly={readOnly}
          disabled={disabled}
        />
      </Stack>
      <Divider sx={{ display: 'block', my: 2 }} />
      <InputText
        label="Address 1"
        nameOverride={nameOverride('address1')}
        type="text"
        floatingLabel={floatingLabel}
        readOnly={readOnly}
        disabled={disabled}
        required
      />
      <Stack direction="row" spacing={1}>
        <InputText
          label="Address 2"
          nameOverride={nameOverride('address2')}
          type="text"
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
        />
        <InputSelect
          label="Address Type"
          options={['Residential', 'Commercial', 'Unknown']}
          nameOverride={nameOverride('type')}
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
          required
          sx={{ flexBasis: 0.3, minWidth: 160 }}
        />
      </Stack>
      <Stack direction="row" spacing={1}>
        <InputText
          label="City"
          nameOverride={nameOverride('city')}
          type="text"
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
          required
        />
        <InputSelect
          label="State"
          options={map(states, (s) => ({
            value: s.code,
            label: s.name,
          }))}
          nameOverride={nameOverride('state')}
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
          required
        />
        <InputText
          label="Zip"
          nameOverride={nameOverride('postcode')}
          type="text"
          floatingLabel={floatingLabel}
          readOnly={readOnly}
          disabled={disabled}
          required
        />
      </Stack>
    </Stack>
  );
}
